import React, {FC, useCallback, useState} from 'react';

import {PLACE_OF_INTERACTION} from '@optimaxdev/analytics/desktop/constants/placeOfInteraction';
import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {Modal, CtaButton, ColorCircle} from '@optimaxdev/design-system';
import {Icon, AdditionalInfo} from '@optimaxdev/icons';
import {connect} from 'react-redux';

import {ACCESSORY_CLICK} from 'constants/accessory';
import {CART_BUTTON} from 'constants/analytics/events/cart';
import {ApplicationStoreType} from 'desktop/store';
import {sessionStore} from 'libs/storage/storage';
import {spawnPriorityTask} from 'libs/timers/spawnPriorityTask/spawnPriorityTask';

import {addAccessory} from '../../store/reducers/accessories/accessories';
import {AccessoryType} from '../../store/reducers/accessories/accessoriesType';
import {changeItemQtyAction} from '../../store/reducers/changeItemQty/changeItemQty';
import {setPlaceOfInteraction} from '../../store/reducers/placeOfInteraction/placeOfInteraction';
import {getCartItemByProductId} from '../../store/selectors';
import {CartItemAccessoryType} from '../../types';
import {AccessoryPrice} from '../accessoryPrice/accessoryPrice';
import {NewCartAccessoryMedia} from '../newCartAccessoryMedia/newCartAccessoryMedia';

import {type PropsType} from './type';

import s from './accessoryInfoTooltip.scss';

/**
 * Accessory learn more tooltip for cart
 */
export const AccessoryInfoTooltip: FC<PropsType> = ({
    accessory: {description, final_price: specialPrice, id, images, name, price},
    addedAccessory = {} as CartItemAccessoryType,
    addToCart,
    changeQty,
    colorOptions,
    isPopupVisible,
    onColorPick,
    savePlaceOfInteraction,
    setPopupVisibility,
}) => {
    const {id: addedAccessoryId, product_id: productId, qty = 0} = addedAccessory;
    const [activeImageIndex, setActiveImageIndex] = useState(0);

    const onMarkClick = () => {
        spawnPriorityTask(() => {
            if (!sessionStore.get(ACCESSORY_CLICK.LEARN_MORE)) {
                sendDesktopAnalyticsEvent(CART_BUTTON, {
                    action: 'Accessories Section',
                    label: 'Learn More',
                });
                sessionStore.set(ACCESSORY_CLICK.LEARN_MORE, true);
            }
        });
        setPopupVisibility(true);
    };

    const onPopupClose = useCallback(() => {
        spawnPriorityTask(() => {
            if (!sessionStore.get(ACCESSORY_CLICK.CLOSE_POPUP)) {
                sendDesktopAnalyticsEvent(CART_BUTTON, {
                    action: 'Accessories Section',
                    label: 'Learn More - PopUp - Close',
                });
                sessionStore.set(ACCESSORY_CLICK.CLOSE_POPUP, true);
            }
        });
        setPopupVisibility(false);
    }, [setPopupVisibility]);

    /**
     * On add to cart button click
     */
    const onAddToCartClick = () => {
        if (qty && addedAccessoryId) {
            changeQty({
                notificationType: 'Added',
                productId,
                qty: Number(qty) + 1,
                quoteItemId: addedAccessoryId,
            });
        } else {
            addToCart(id);
        }
        savePlaceOfInteraction(PLACE_OF_INTERACTION.ACCESSORIES_LEARN_MORE);
        setPopupVisibility(false);
    };

    return (
        <>
            <button aria-label="info" className={s.mark} onClick={onMarkClick} type="button">
                <Icon icon={AdditionalInfo} size="medium" />
            </button>
            <Modal
                isOpen={isPopupVisible}
                onRequestClose={onPopupClose}
                overlay="semi"
                isAnimationEnabled
            >
                <div className={s.popupWrap}>
                    <NewCartAccessoryMedia
                        activeImageIndex={activeImageIndex}
                        alt={name}
                        changeActiveIndex={setActiveImageIndex}
                        images={images}
                    />
                    <div className={s.info}>
                        <p className={s.title}>{name}</p>
                        <p className={s.price}>
                            <AccessoryPrice price={price} specialPrice={specialPrice} />
                        </p>
                        {colorOptions.length > 1 ? (
                            <ul className={s.colorsList}>
                                {colorOptions.map(color => {
                                    if (!color) return null;

                                    return (
                                        <li className={s.color} key={color.id}>
                                            <ColorCircle
                                                changeSimple={() => {
                                                    setActiveImageIndex(0);
                                                    onColorPick(color);
                                                }}
                                                active={id === color.id}
                                                ariaLabel="ColorCircle"
                                                circleSettings={color}
                                                size="medium"
                                            />
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : null}
                        <p className={s.description}>{description}</p>
                        <CtaButton
                            colorName="secondary"
                            isDisabled={Number(qty) >= 2}
                            onClick={onAddToCartClick}
                            size="medium"
                        >
                            Add to Cart
                        </CtaButton>
                    </div>
                </div>
            </Modal>
        </>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @param {PropsType} props - props
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType, {accessory}: {accessory: AccessoryType}) => ({
    addedAccessory: getCartItemByProductId(store, accessory.id),
});

export const AccessoryInfoTooltipConnected = connect(mapStateToProps, {
    addToCart: addAccessory,
    changeQty: changeItemQtyAction,
    savePlaceOfInteraction: setPlaceOfInteraction,
})(AccessoryInfoTooltip);
